import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
    fetchSalesReportData,
    setFromDate,
    setToDate,
    setSelectedBranch,
    resetFilters,
    clearSalesData,
    selectSelectedBranch,
    selectFromDate,
    selectToDate,
    selectLoading
} from '../../store/slices/salesReportsSlice';
import { getBranches } from '../../utilities/api';
import useUser from '../../hooks/useUser';
import './billreports.css';

const SalesReportsHeader = () => {
    const dispatch = useDispatch();
    const { userRole } = useUser();

    // Redux selectors
    const selectedBranch = useSelector(selectSelectedBranch);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const loading = useSelector(selectLoading);

    // Get role from localStorage
    const role = userRole || localStorage.getItem('role') || '';
    const roleLower = role?.toLowerCase() || '';
    const isManager = roleLower === 'manager';
    const isAdmin = roleLower === 'admin';
    const isCEO = roleLower === 'ceo';

    // Role-based access control
    const canAccessDateRange = isAdmin || isCEO;
    const canAccessBranch = isAdmin || isCEO;

    // Local state for branches
    const [branches, setBranches] = React.useState([]);
    const [branchesLoading, setBranchesLoading] = React.useState(false);

    // Initialize dates - Manager gets today, Admin/CEO remain blank by default
    useEffect(() => {
        if (isManager) {
            const today = new Date();
            dispatch(setFromDate(today));
            dispatch(setToDate(today));
        }
    }, [isManager, dispatch]);

    // Fetch branches for Admin/CEO
    useEffect(() => {
        if (canAccessBranch) {
            const fetchBranches = async () => {
                try {
                    setBranchesLoading(true);
                    const clientId = localStorage.getItem('clientid');
                    if (clientId) {
                        const branchList = await getBranches(clientId);
                        // Add "All Branches" option
                        const branchesWithAll = [
                            { branch_id: 'All', branch_name: 'All Branches' },
                            ...(Array.isArray(branchList) ? branchList : [])
                        ];
                        setBranches(branchesWithAll);
                        
                        // Set default to "All Branches"
                        if (!selectedBranch) {
                            dispatch(setSelectedBranch(branchesWithAll[0]));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branches:', error);
                    setBranches([{ branch_id: 'All', branch_name: 'All Branches' }]);
                } finally {
                    setBranchesLoading(false);
                }
            };
            fetchBranches();
        } else {
            // Manager - set their branch
            const managerBranchId = localStorage.getItem('branchid');
            const managerBranchName = localStorage.getItem('branchname') || 'Current Branch';
            if (managerBranchId) {
                setBranches([{ branch_id: managerBranchId, branch_name: managerBranchName }]);
                dispatch(setSelectedBranch({ branch_id: managerBranchId, branch_name: managerBranchName }));
            }
        }
    }, [canAccessBranch, dispatch]);

    // Auto-fetch data when filters change (for manager, fetch immediately)
    useEffect(() => {
        if (isManager && fromDate && toDate) {
            const branchId = selectedBranch?.branch_id || localStorage.getItem('branchid') || 'All';
            dispatch(
                fetchSalesReportData({
                    fromDate,
                    toDate,
                    branchId
                })
            );
        }
    }, [isManager, fromDate, toDate, selectedBranch, dispatch]);

    // Apply filters - fetch sales data
    const handleApplyFilters = () => {
        if (!fromDate || !toDate) {
            return;
        }

        const branchId = selectedBranch?.branch_id || 'All';
        dispatch(
            fetchSalesReportData({
                fromDate,
                toDate,
                branchId
            })
        );
    };

    // Reset filters
    const handleResetFilters = () => {
        dispatch(resetFilters());
        // Keep manager date behavior unchanged after reset
        if (isManager) {
            const today = new Date();
            dispatch(setFromDate(today));
            dispatch(setToDate(today));
        }
    };

    return (
        <>
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Sales Reports
                </h2>
                <p className="text-color-secondary">View and analyze sales reports with advanced filtering options</p>
            </div>

            <Card className="mb-3 sales-reports-filter-card">
                <div className="flex flex-wrap align-items-end sales-reports-filter-row">
                    {canAccessDateRange && (
                        <>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="fromDate">From Date <span className="text-red-500">*</span></label>
                                <Calendar id="fromDate" value={fromDate} onChange={(e) => dispatch(setFromDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                            </div>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="toDate">To Date <span className="text-red-500">*</span></label>
                                <Calendar id="toDate" value={toDate} onChange={(e) => dispatch(setToDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                            </div>
                        </>
                    )}

                    {!canAccessDateRange && (
                        <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                            <label htmlFor="date">Date</label>
                            <Calendar id="date" value={fromDate} readOnly dateFormat="dd-mm-yy" showIcon className="w-full" disabled />
                        </div>
                    )}

                    {canAccessBranch && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown id="branch" value={selectedBranch} options={branches} onChange={(e) => dispatch(setSelectedBranch(e.value))} optionLabel="branch_name" placeholder="All Branches" className="w-full" filter showClear loading={branchesLoading || undefined} />
                        </div>
                    )}

                    {!canAccessBranch && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown id="branch" value={selectedBranch} options={branches} disabled optionLabel="branch_name" className="w-full" />
                        </div>
                    )}

                    {canAccessDateRange && (
                        <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                            <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} disabled={(!fromDate || !toDate) || loading} loading={loading} />
                            <Button icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} tooltip="Reset" />
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default SalesReportsHeader;
